import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Task, TaskToShiftsResult } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { useAssignees } from './assignees';
import { useAppointmentTypes } from './appointmentTypes';
import { RelatedOrders } from '../orders/RelatedOrders';
import './calendar.css';

const dateTime = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long', timeStyle: 'short' });
const BACK = '/kalender/aufgaben';

/**
 * Aufgabe anlegen oder bearbeiten. Beim Bearbeiten lässt sie sich auch erledigen und, mit dem Recht dazu, löschen. Das Feld „Report“
 * und das Erledigen stehen außerdem der zuständigen Person offen, auch wenn ihre Rolle Aufgaben sonst nur lesen darf. Alle anderen
 * Felder bleiben für sie gesperrt.
 */
export function TaskFormPage() {
  const { id } = useParams();
  const isCreate = !id;
  const [params] = useSearchParams();
  // Aus einem Auftrag heraus (KON-128): Die neue Aufgabe wird danach mit ihm verknüpft, und es geht zurück zum Auftrag
  const orderId = isCreate ? params.get('auftrag') : null;
  const back = orderId ? `/auftraege/${orderId}` : BACK;
  const navigate = useNavigate();
  const client = useQueryClient();
  const { can, session, hasModule } = useSession();
  const canWrite = can('calendar.write');
  const assignees = useAssignees();
  const types = useAppointmentTypes();

  const existing = useQuery({ queryKey: ['task', id], queryFn: async () => (await api<{ data: Task }>('GET', `/tasks/${id}`)).data, enabled: !isCreate });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [addingPerson, setAddingPerson] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [participantUserIds, setParticipantUserIds] = useState<string[]>([]);
  const [report, setReport] = useState('');
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [calendarNote, setCalendarNote] = useState<string | null>(null);
  const [calendarOnCreate, setCalendarOnCreate] = useState(false);
  const calendarFailed = useRef(false);
  const loaded = useRef(false);

  useEffect(() => {
    const t = existing.data;
    if (loaded.current || !t) return;
    loaded.current = true;
    setTitle(t.title);
    setDescription(t.description ?? '');
    setDueDate(t.dueDate ?? '');
    setStartDate(t.startDate ?? '');
    setEndDate(t.endDate ?? '');
    setStartTime(t.startTime ?? '');
    setEndTime(t.endTime ?? '');
    setTypeId(t.typeId ?? '');
    setAssignedUserId(t.assignedUserId ?? '');
    setReport(t.report ?? '');
    setParticipantUserIds(t.participantUserIds);
    setDone(t.status === 'done');
  }, [existing.data]);

  const onError = (e: unknown) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (e instanceof ApiError) {
      setErrors(e.status === 422 ? e.fields : {});
      setFormError(e.status === 422 ? 'Bitte prüfen Sie Ihre Eingaben.' : e.message);
    } else {
      setFormError('Die Aufgabe konnte nicht gespeichert werden.');
    }
  };

  const save = useMutation({
    /** finish: Speichern und gleichzeitig als erledigt markieren */
    mutationFn: async (finish?: boolean) => {
      const status = finish || done ? 'done' : 'open';
      const fields = { title: title.trim(), description: description.trim(), dueDate, startDate, endDate, startTime, endTime };
      // Beim Anlegen bleibt ein leeres Feld einfach leer, beim Ändern löscht eine leere Eingabe den Wert
      if (isCreate) {
        const created = await api<{ data: Task }>('POST', '/tasks', { ...fields, ...(typeId ? { typeId } : {}), ...(assignedUserId ? { assignedUserId } : {}), ...(participantUserIds.length > 0 ? { participantUserIds } : {}) });
        if (orderId) await api('POST', `/orders/${orderId}/tasks`, { taskId: created.data.id });
        // Auf Wunsch gleich als Termin in den Kalender übernehmen. Klappt das nicht, bleibt die Aufgabe bestehen und der Knopf steht dort bereit.
        if (calendarOnCreate) {
          try {
            await api('POST', `/tasks/${created.data.id}/calendar`);
          } catch {
            calendarFailed.current = true;
          }
        }
        return created;
      }
      // Wer die Aufgabe nur als zuständige Person bearbeitet, nutzt den Weg, der nur Report und Status ändert
      if (!canWrite) return api<{ data: Task }>('PATCH', `/tasks/${id}/progress`, { report: report.trim(), status });
      const updated = await api<{ data: Task }>('PATCH', `/tasks/${id}`, { ...fields, typeId: typeId || null, assignedUserId: assignedUserId || null, participantUserIds, status });
      // Den Report schreibt nur die zuständige Person, auch wenn sie zum Büro gehört, und nur solange sie es nach dem Speichern noch ist
      const reportChanged = report.trim() !== (existing.data?.report ?? '');
      if (reportChanged && isAssignee && assignedUserId === session?.user.id) return api<{ data: Task }>('PATCH', `/tasks/${id}/progress`, { report: report.trim() });
      return updated;
    },
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['tasks'] });
      client.invalidateQueries({ queryKey: ['calendar-tasks'] });
      client.invalidateQueries({ queryKey: ['appointments'] });
      client.removeQueries({ queryKey: ['task', id] });
      if (orderId) client.invalidateQueries({ queryKey: ['order', orderId] });
      navigate(calendarFailed.current ? `/kalender/aufgaben/${res.data.id}` : back, { replace: true });
    },
    onError,
  });
  const toCalendar = useMutation({
    mutationFn: () => api<{ data: { appointmentId: string } }>('POST', `/tasks/${id}/calendar`),
    onSuccess: () => {
      for (const key of ['task', 'tasks', 'calendar-tasks', 'appointments']) client.invalidateQueries({ queryKey: [key] });
      setCalendarNote('Die Aufgabe steht jetzt als Termin im Kalender.');
    },
    onError: (e) => setCalendarNote(e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Die Übernahme in den Kalender hat nicht geklappt.'),
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/tasks/${id}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['tasks'] });
      client.removeQueries({ queryKey: ['task', id] });
      navigate(BACK, { replace: true });
    },
    onError,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    calendarFailed.current = false;
    if (isCreate && calendarOnCreate && !startDate && !endDate && !dueDate) {
      setErrors({ startDate: 'Für die Übernahme in den Kalender braucht die Aufgabe ein Start- oder Enddatum.' });
      setFormError('Bitte prüfen Sie Ihre Eingaben.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    save.mutate(false);
  }

  if (!isCreate && existing.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && (existing.isError || !existing.data)) {
    return (
      <div className="card narrow">
        <h1>Aufgabe nicht gefunden</h1>
        <Link to={BACK}>Zur Übersicht</Link>
      </div>
    );
  }

  const t = existing.data;
  const isAssignee = !isCreate && !!session && t?.assignedUserId === session.user.id;
  // Report und Erledigen: das Büro und die zuständige Person
  const canProgress = !isCreate && (canWrite || isAssignee);
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  // Ein Zuständiger, der nicht mehr in der Auswahl steht (deaktiviert), bleibt sichtbar, bis jemand anderes gewählt wird
  const missingAssignee = assignedUserId && !assignees.list.some((a) => a.id === assignedUserId);
  // Wer noch als Beteiligter zur Wahl steht: nicht die zuständige Person und niemand, der schon in der Liste ist
  const remainingPeople = assignees.list.filter((a) => a.id !== assignedUserId && !participantUserIds.includes(a.id));
  // In den Kalender übernehmen: die zuständige Person, die Beteiligten, die Schichtplanung und wer Termine verwalten darf
  const planner = hasModule('shifts') && can('shifts.write');
  const mayCalendar = !isCreate && !!session && (canWrite || planner || t?.assignedUserId === session.user.id || !!t?.participantUserIds.includes(session.user.id));

  return (
    <>
      <p><Link to={back} className="back-link">← {orderId ? 'Zum Auftrag' : 'Alle Aufgaben'}</Link></p>
      <div className="page-head">
        <h1>{isCreate ? 'Neue Aufgabe' : t!.title}</h1>
      </div>
      {!isCreate && t!.status === 'done' && t!.completedAt && (
        <p className="muted">
          Erledigt am {dateTime.format(new Date(t!.completedAt))}
          {assignees.name(t!.completedByUserId) ? ` von ${assignees.name(t!.completedByUserId)}` : ''}.
        </p>
      )}

      <form className="card task-form" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}
        <div className="task-form-row">
          <label className="field">
            <span>Titel <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} autoComplete="off" disabled={!canWrite} aria-invalid={!!errors.title} />
            {error('title')}
          </label>
          <label className="field">
            <span>Aufgabentyp</span>
            <select className="input" value={typeId} onChange={(e) => setTypeId(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.typeId}>
              <option value="">Kein Typ</option>
              {types.list.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              {typeId && !types.find(typeId) && <option value={typeId}>Nicht mehr verfügbar</option>}
            </select>
            {error('typeId')}
          </label>
        </div>
        <div className="task-form-row task-form-row--time">
          <label className="field">
            <span>Startdatum</span>
            <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.startDate} />
            {error('startDate')}
          </label>
          <label className="field">
            <span>Uhrzeit</span>
            <input className="input" type="time" step={900} value={startTime} onChange={(e) => setStartTime(e.target.value)} disabled={!canWrite} aria-label="Uhrzeit des Starts" aria-invalid={!!errors.startTime} />
            {error('startTime')}
          </label>
        </div>
        <div className="task-form-row task-form-row--time">
          <label className="field">
            <span>Enddatum</span>
            <input className="input" type="date" value={endDate} min={startDate || undefined} onChange={(e) => setEndDate(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.endDate} />
            {error('endDate')}
          </label>
          <label className="field">
            <span>Uhrzeit</span>
            <input className="input" type="time" step={900} value={endTime} onChange={(e) => setEndTime(e.target.value)} disabled={!canWrite} aria-label="Uhrzeit des Endes" aria-invalid={!!errors.endTime} />
            {error('endTime')}
          </label>
        </div>
        <div className="task-form-row task-form-row--assignee">
          <label className="field">
            <span>Zuständig</span>
            <select
              className="input"
              value={assignedUserId}
              onChange={(e) => {
                setAssignedUserId(e.target.value);
                // Die zuständige Person steht nie zugleich unter den Beteiligten
                setParticipantUserIds((ids) => ids.filter((x) => x !== e.target.value));
              }}
              disabled={!canWrite}
              aria-invalid={!!errors.assignedUserId}
            >
              <option value="">Niemand</option>
              {assignees.list.map((a) => <option key={a.id} value={a.id}>{a.name}{a.id === session?.user.id ? ' (ich)' : ''}</option>)}
              {missingAssignee && <option value={assignedUserId}>Nicht mehr verfügbar</option>}
            </select>
            {error('assignedUserId')}
          </label>
          {canWrite && (
            <div className="field task-add">
              <span aria-hidden="true">&nbsp;</span>
              <button type="button" className="btn btn--secondary" aria-expanded={addingPerson} onClick={() => setAddingPerson((v) => !v)} disabled={remainingPeople.length === 0}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
                Beteiligte hinzufügen
              </button>
            </div>
          )}
        </div>
        {addingPerson && canWrite && (
          <label className="field task-add-select">
            <span>Mitarbeiter wählen</span>
            <select
              className="input"
              value=""
              autoFocus
              onChange={(e) => {
                if (e.target.value) setParticipantUserIds((ids) => [...ids, e.target.value]);
                setAddingPerson(false);
              }}
            >
              <option value="">Bitte wählen</option>
              {remainingPeople.map((a) => <option key={a.id} value={a.id}>{a.name}{a.id === session?.user.id ? ' (ich)' : ''}</option>)}
            </select>
          </label>
        )}
        <div className="field">
          <span className="field__label">Beteiligte Mitarbeiter</span>
          {participantUserIds.length === 0 ? (
            <small className="muted">Noch niemand beteiligt. Beteiligte bilden das Team ab und sehen die Aufgabe, bearbeiten dürfen sie nur die zuständige Person und das Büro.</small>
          ) : (
            <ul className="task-people">
              {participantUserIds.map((pid) => (
                <li key={pid}>
                  <span>{assignees.name(pid) ?? 'Nicht mehr verfügbar'}{pid === session?.user.id ? ' (ich)' : ''}</span>
                  {canWrite && <button type="button" className="task-people__remove" onClick={() => setParticipantUserIds((ids) => ids.filter((x) => x !== pid))} aria-label={`${assignees.name(pid) ?? 'Person'} entfernen`}>×</button>}
                </li>
              ))}
            </ul>
          )}
          {error('participantUserIds')}
        </div>
        {isCreate && canWrite && (
          <label className="check task-calendar-check">
            <input type="checkbox" checked={calendarOnCreate} onChange={(e) => setCalendarOnCreate(e.target.checked)} />
            <span>
              In den Kalender übernehmen
              <small className="muted"> Legt beim Anlegen einen Termin für die zuständige Person und die Beteiligten an, mit Ihrer Uhrzeit (sonst ganztägig). Ohne Häkchen erscheint die Aufgabe nicht im Kalender, Sie können es später mit dem Knopf nachholen.</small>
            </span>
          </label>
        )}
        <div className="task-form-row">
          <label className="field">
            <span>Fällig am</span>
            <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={!canWrite} aria-invalid={!!errors.dueDate} />
            {error('dueDate')}
          </label>
        </div>
        <label className="field">
          <span>Beschreibung</span>
          <textarea className="input textarea" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} disabled={!canWrite} />
          {error('description')}
        </label>
        {!isCreate && (
          <label className="field">
            <span>Report der zuständigen Person</span>
            <textarea className="input textarea" value={report} onChange={(e) => setReport(e.target.value)} maxLength={10000} disabled={!isAssignee} placeholder={isAssignee ? 'Notizen zur Arbeit und Bericht zur Erledigung' : 'Noch kein Report'} aria-invalid={!!errors.report} />
            {error('report')}
            <small className="muted">
              {isAssignee
                ? 'Sie sind für diese Aufgabe zuständig und schreiben hier Notizen und den Bericht zur Erledigung.'
                : 'Den Report schreibt nur die zuständige Person dieser Aufgabe. Sie sehen ihn, können ihn aber nicht ändern.'}
            </small>
          </label>
        )}
        {!isCreate && (
          <label className="check">
            <input type="checkbox" checked={done} disabled={!canProgress} onChange={(e) => setDone(e.target.checked)} />
            <span>Erledigt</span>
          </label>
        )}

        <div className="form-actions">
          {(isCreate ? canWrite : canProgress) && (
            <button className="btn btn--primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Wird gespeichert …' : isCreate ? 'Aufgabe anlegen' : 'Speichern'}
            </button>
          )}
          {canProgress && !done && (
            <button className="btn btn--secondary" type="button" disabled={save.isPending} onClick={() => { setErrors({}); setFormError(null); setDone(true); save.mutate(true); }}>
              Speichern und als erledigt markieren
            </button>
          )}
          <Link className="btn btn--secondary" to={back}>{canProgress || isCreate ? 'Abbrechen' : 'Zurück'}</Link>
        </div>
        {!isCreate && planner && <ShiftAdoption taskId={id!} hasTimes={!!t?.startTime && !!t?.endTime} inPlan={!!t?.inShiftPlan} />}
        {mayCalendar && (
          <div className="task-calendar">
            {t?.appointmentId ? (
              <p className="muted small">Diese Aufgabe steht im Kalender. <Link to={`/kalender/termine/${t.appointmentId}`}>Termin öffnen</Link>. Änderungen an Datum oder Uhrzeit der Aufgabe übernehmen Sie dort von Hand.</p>
            ) : (
              <>
                <button type="button" className="btn btn--secondary" disabled={toCalendar.isPending} onClick={() => { setCalendarNote(null); toCalendar.mutate(); }}>
                  {toCalendar.isPending ? 'Wird übernommen …' : 'In den Kalender übernehmen'}
                </button>
                <small className="muted">Legt einen Termin für die zuständige Person und alle Beteiligten an. Speichern Sie Änderungen an Datum und Uhrzeit vorher.</small>
              </>
            )}
            {calendarNote && <p className="small" role="status">{calendarNote}</p>}
          </div>
        )}
      </form>

      {!isCreate && hasModule('orders') && can('orders.read') && <RelatedOrders taskId={id} />}

      {!isCreate && can('calendar.delete') && (
        <div className="danger-zone">
          <button
            className="btn btn--danger btn--sm"
            type="button"
            disabled={remove.isPending}
            onClick={() => {
              if (window.confirm(`Aufgabe „${t!.title}“ wirklich löschen?`)) remove.mutate();
            }}
          >
            Aufgabe löschen
          </button>
        </div>
      )}
    </>
  );
}

const dayDe = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}`;

/**
 * Aufgabe in den Schichtplan übernehmen (nur für die Schichtplanung). Die Aufgabe erscheint dort als zusätzliche Ebene über dem Dienst der Beteiligten, es entstehen keine neuen
 * Schichten. Der Knopf prüft zuerst, bei wem die Aufgabe in einen veröffentlichten Dienst passt und bei wem nicht (abwesend, kein oder unveröffentlichter Dienst, Aufgabe außerhalb
 * des Dienstes), mit ausformuliertem Hinweis je Person. Erst die Bestätigung übernimmt die Aufgabe.
 */
function ShiftAdoption({ taskId, hasTimes, inPlan }: { taskId: string; hasTimes: boolean; inPlan: boolean }) {
  const client = useQueryClient();
  const [result, setResult] = useState<TaskToShiftsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => {
    for (const key of ['shifts', 'task', 'tasks']) client.invalidateQueries({ queryKey: [key] });
  };
  const run = useMutation({
    mutationFn: async (confirm: boolean) => (await api<{ data: TaskToShiftsResult }>('POST', '/shifts/from-task', { taskId, confirm })).data,
    onSuccess: (data) => {
      setError(null);
      setResult(data);
      if (data.applied) refresh();
    },
    onError: (e) => {
      setResult(null);
      setError(e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Die Übernahme in den Schichtplan hat nicht geklappt.');
    },
  });
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/shifts/from-task/${taskId}`),
    onSuccess: () => {
      setResult(null);
      refresh();
    },
    onError: () => setError('Die Aufgabe konnte nicht aus dem Schichtplan genommen werden.'),
  });
  const people = result ? new Set(result.fits.map((p) => p.employeeId)).size : 0;
  return (
    <div className="task-calendar task-shifts">
      {inPlan && !result && (
        <p className="small">
          Diese Aufgabe liegt im <Link to="/schichtplanung">Schichtplan</Link> als Überlagerung über den Diensten der Beteiligten.
        </p>
      )}
      <div className="task-shifts__buttons">
        <button type="button" className="btn btn--secondary" disabled={run.isPending || !hasTimes} onClick={() => run.mutate(false)} title={hasTimes ? undefined : 'Tragen Sie zuerst Start- und Enduhrzeit ein und speichern Sie'}>
          {run.isPending && !result ? 'Wird geprüft …' : inPlan ? 'Im Schichtplan neu prüfen' : 'In den Schichtplan übernehmen'}
        </button>
        {inPlan && (
          <button type="button" className="btn btn--secondary" disabled={remove.isPending} onClick={() => remove.mutate()}>
            Aus dem Schichtplan nehmen
          </button>
        )}
      </div>
      <small className="muted">{hasTimes ? 'Prüft zuerst, bei wem die Aufgabe in einen veröffentlichten Dienst passt. Übernommen wird erst nach Ihrer Bestätigung, es entstehen keine neuen Schichten.' : 'Dafür braucht die Aufgabe Start- und Enduhrzeit (gespeichert).'}</small>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {result && (
        <div className="task-shifts__result" role="region" aria-label="Vorschau Schichtplan">
          {result.applied ? (
            <div className="alert alert--ok" role="status">
              Die Aufgabe liegt jetzt im <Link to="/schichtplanung">Schichtplan</Link>, bei {people} {people === 1 ? 'Person' : 'Personen'} als Überlagerung über dem Dienst.
            </div>
          ) : (
            <strong>{result.fits.length === 0 ? 'Die Aufgabe passt bei niemandem in einen veröffentlichten Dienst.' : `Die Aufgabe passt bei ${people} ${people === 1 ? 'Person' : 'Personen'} in den Dienst:`}</strong>
          )}
          {result.fits.length > 0 && (
            <ul>
              {result.fits.map((p) => <li key={`${p.employeeId}-${p.date}`}>{p.employeeName}: {dayDe(p.date)}, Aufgabe {p.startTime} bis {p.endTime} Uhr im Dienst von {p.shiftStart} bis {p.shiftEnd} Uhr</li>)}
            </ul>
          )}
          {result.blocked.length > 0 && (
            <div className="alert alert--warn" role="alert">
              <strong>Nicht möglich für:</strong>
              <ul>{result.blocked.map((b, i) => <li key={`${b.employeeId ?? i}-${b.date ?? 'alle'}-${i}`}>{b.message}</li>)}</ul>
              {!result.applied && result.fits.length > 0 && <p className="small">Bei diesen Personen erscheint die Aufgabe nicht im Schichtplan. Passen Sie ihren Dienst an oder lassen Sie sie aus der Aufgabe heraus.</p>}
            </div>
          )}
          {result.skippedDays.length > 0 && <p className="muted small">Nicht geprüft: {result.skippedDays.map((d) => `${dayDe(d.date)} (${d.reason})`).join(', ')}.</p>}
          {!result.applied && (
            <div className="form-actions">
              {result.fits.length > 0 && <button type="button" className="btn btn--primary" disabled={run.isPending} onClick={() => run.mutate(true)}>{run.isPending ? 'Wird übernommen …' : `Für ${people} ${people === 1 ? 'Person' : 'Personen'} übernehmen`}</button>}
              <button type="button" className="btn btn--secondary" onClick={() => setResult(null)}>Schließen</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
